import { useEffect, useState } from 'react';
import {
  useListContractEstimatesQuery,
  useListContractsQuery,
  useUploadContractEstimateMutation,
} from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { Combobox } from '@shared/ui/Combobox';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import { ParseWarningsBanner } from '../ParseWarningsBanner';
import { apiErr } from './ksCheckLabels';
import {
  CheckboxLabel,
  Err,
  FileHint,
  Muted,
  MutedMargin,
  ObjName,
  SubmitWrapper,
  Table,
  TableTitle,
} from './styled';

interface BenchmarkUploadCardProps {
  contractId: string;
  setContractId: (id: string) => void;
  canWrite: boolean;
}

export function BenchmarkUploadCard({
  contractId,
  setContractId,
  canWrite,
}: BenchmarkUploadCardProps) {
  const [contractLabel, setContractLabel] = useState('');
  const [contractQuery, setContractQuery] = useState('');
  const [estFile, setEstFile] = useState<File | null>(null);
  const [estFormat, setEstFormat] = useState('lsr');
  const [estIsAmendment, setEstIsAmendment] = useState(false);
  const [estComment, setEstComment] = useState('');
  const [estError, setEstError] = useState<string | null>(null);
  const [estWarnings, setEstWarnings] = useState<string[]>([]);

  const { setActiveProject } = useActiveProjectContext();

  const estimatesQ = useListContractEstimatesQuery(
    { contractId: contractId.trim() },
    { skip: contractId.trim().length < 8 },
  );
  // Серверный поиск договоров (по номеру / контрагенту / предмету) — список
  // растёт, клиентский фильтр не масштабируется. Пустой запрос → свежие 20.
  const contractsQ = useListContractsQuery({
    search: contractQuery.trim() || undefined,
    limit: 20,
  });
  const [uploadEstimate, { isLoading: uploadingEst }] = useUploadContractEstimateMutation();

  const estimates = estimatesQ.data?.items ?? [];
  const contracts = contractsQ.data?.items ?? [];
  const contractOptions = contracts.map((c) => ({
    id: c.id,
    label: c.contract_number ? `№ ${c.contract_number} · ${c.counterparty}` : c.counterparty,
    sublabel: c.subject ?? undefined,
  }));

  useEffect(() => {
    if (!contractId) return;
    const c = contractsQ.data?.items.find((x) => x.id === contractId);
    if (c?.project_id) {
      setActiveProject(c.project_id);
    }
  }, [contractId, contractsQ.data?.items, setActiveProject]);

  async function handleUploadEstimate(e: React.FormEvent) {
    e.preventDefault();
    setEstError(null);
    setEstWarnings([]);
    if (!contractId.trim()) {
      setEstError('Выберите договор из списка.');
      return;
    }
    if (!estFile) {
      setEstError('Приложите файл договорной сметы (xlsx).');
      return;
    }
    const fd = new FormData();
    fd.append('file', estFile);
    fd.append('contract_id', contractId.trim());
    fd.append('source_format', estFormat);
    if (estIsAmendment) fd.append('is_amendment', 'true');
    if (estComment.trim()) fd.append('comment', estComment.trim());
    try {
      const res = await uploadEstimate(fd).unwrap();
      setEstWarnings(res.warnings ?? []);
      setEstFile(null);
      setEstIsAmendment(false);
      setEstComment('');
    } catch (err) {
      setEstError(apiErr(err, 'Не удалось загрузить договорную смету.'));
      // eslint-disable-next-line no-console
      console.error('uploadContractEstimate failed', err);
    }
  }

  return (
    <Card>
      <form onSubmit={handleUploadEstimate}>
        <ObjName>1. Договорная смета (эталон)</ObjName>
        <Field>
          <Label>Договор (из агента Статут)</Label>
          <Combobox
            id="ks-contract"
            value={contractId}
            selectedLabel={contractLabel}
            options={contractOptions}
            onQueryChange={setContractQuery}
            loading={contractsQ.isFetching}
            placeholder="Начните вводить № или название договора"
            emptyText="Договоры не найдены"
            onChange={(opt) => {
              setContractId(opt?.id ?? '');
              setContractLabel(opt?.label ?? '');
            }}
          />
          <FileHint>
            Поиск по номеру / названию / контрагенту (из агента Статут). Нет договора? Сначала
            загрузите его в Статуте.
          </FileHint>
        </Field>
        <Field>
          <Label>Файл сметы</Label>
          <Input
            type="file"
            accept={estFormat === 'boq' ? '.xlsx,.xlsm,.pdf' : '.xlsx,.xlsm'}
            onChange={(e) => setEstFile(e.target.files?.[0] ?? null)}
          />
          <FileHint>
            ЛСР «17 граф» (Гранд-Смета) — только Excel (xlsx). РДЦ / BOQ (формат A) — Excel
            или PDF.
          </FileHint>
        </Field>
        <Field>
          <Label>Формат</Label>
          <Select value={estFormat} onChange={(e) => setEstFormat(e.target.value)}>
            <option value="lsr">ЛСР (Гранд-Смета)</option>
            <option value="boq">РДЦ / BOQ (формат A)</option>
          </Select>
        </Field>
        <Field>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={estIsAmendment}
              onChange={(e) => setEstIsAmendment(e.target.checked)}
            />
            Новая версия по доп. соглашению (ДС)
          </CheckboxLabel>
        </Field>
        <Field>
          <Label>Комментарий (необязательно)</Label>
          <Input
            value={estComment}
            onChange={(e) => setEstComment(e.target.value)}
            placeholder="напр. РДЦ по подписанному ДС №… / по подписанным ЗВИ"
          />
          <FileHint>Помогает отличать версии (ДС) и документы в списке выбора.</FileHint>
        </Field>
        {estError && <Err>{estError}</Err>}
        <ParseWarningsBanner warnings={estWarnings} />
        <SubmitWrapper>
          <Button
            type="submit"
            $variant="primary"
            disabled={uploadingEst || !canWrite}
            title={!canWrite ? 'Только чтение' : undefined}
          >
            {uploadingEst ? 'Загрузка…' : 'Загрузить эталон'}
          </Button>
        </SubmitWrapper>
      </form>
      {contractId.trim().length >= 8 && (
        <>
          <TableTitle>
            Версии эталона <Badge $tone="neutral">{estimates.length}</Badge>
          </TableTitle>
          {estimates.length === 0 ? (
            <MutedMargin>
              Для этого договора эталон ещё не загружен.
            </MutedMargin>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Версия</th>
                  <th>Формат</th>
                  <th>Статус</th>
                  <th>Загружена</th>
                  <th>Комментарий</th>
                </tr>
              </thead>
              <tbody>
                {estimates.map((es) => (
                  <tr key={es.id}>
                    <td>v{es.version_no}</td>
                    <td>{es.source}</td>
                    <td>{es.status}</td>
                    <td>
                      <Muted>{new Date(es.created_at).toLocaleDateString('ru-RU')}</Muted>
                    </td>
                    <td>
                      <Muted>{es.comment ?? '—'}</Muted>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </Card>
  );
}
